import * as React from "react";

interface SwitchProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  // Radix-compat passthrough props (ignored but accepted so existing call-sites don't error)
  name?: string;
  value?: string;
  required?: boolean;
  form?: string;
  asChild?: boolean;
}

const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      checked: controlledChecked,
      defaultChecked = false,
      onCheckedChange,
      disabled = false,
      id,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      // swallow unused Radix props
      name: _name,
      value: _value,
      required: _required,
      form: _form,
      asChild: _asChild,
      className: _className,
      ...rest
    },
    ref
  ) => {
    const isControlled = controlledChecked !== undefined;
    const [internalChecked, setInternalChecked] = React.useState(defaultChecked);
    const checked = isControlled ? controlledChecked : internalChecked;

    const handleClick = () => {
      if (disabled) return;
      const next = !checked;
      if (!isControlled) setInternalChecked(next);
      onCheckedChange?.(next);
    };

    return (
      <button
        ref={ref}
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        disabled={disabled}
        onClick={handleClick}
        style={{
          width: 40,
          height: 22,
          borderRadius: 11,
          border: "1px solid rgba(255,255,255,0.2)",
          background: checked ? "#1d9e75" : "rgba(255,255,255,0.06)",
          position: "relative",
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "background 150ms ease",
          flexShrink: 0,
          outline: "none",
          padding: 0,
          opacity: disabled ? 0.5 : 1,
          display: "inline-block",
        }}
        {...rest}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: checked ? 20 : 2,
            width: 16,
            height: 16,
            borderRadius: "50%",
            background: checked ? "#000000" : "rgba(255,255,255,0.5)",
            transition: "left 150ms ease",
            pointerEvents: "none",
          }}
        />
      </button>
    );
  }
);

Switch.displayName = "Switch";

export { Switch };
